import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { useTheme } from '../contexts/ThemeContext';
import { getMe, getWalletTransactions, getWallet, getNotifications, markNotificationRead, createWithdrawal } from '../api/api';
import { UserOut, PaymentOut, WalletOut, NotificationOut } from '../api/types';
import { formatNotification } from '../utils/notificationFormatter';

interface DesktopAccountPageProps {
  onLoginClick: () => void;
}

const DesktopAccountPage: React.FC<DesktopAccountPageProps> = ({ onLoginClick }) => {
  const { t, locale } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [user, setUser] = useState<UserOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<PaymentOut[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txFilter, setTxFilter] = useState<'all' | 'deposit' | 'withdrawal'>('all');
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [wallet, setWallet] = useState<WalletOut | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [recentTx, setRecentTx] = useState<PaymentOut[]>([]);
  const [notifications, setNotifications] = useState<NotificationOut[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Withdrawal States
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<string>('Credit Card');
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [withdrawalError, setWithdrawalError] = useState<string | null>(null);
  const [completedWithdrawal, setCompletedWithdrawal] = useState<PaymentOut | null>(null);

  // Contact States
  const [contactSubject, setContactSubject] = useState('Allgemein');
  const [contactMessage, setContactMessage] = useState('');
  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactMessage.trim()) return;
    setContactStatus('loading');
    setTimeout(() => {
      setContactStatus('success');
      setContactMessage('');
    }, 1000);
  };

  const [activeSubTab, setActiveSubTab] = useState<'personal' | 'contact' | 'password'>('personal');

  const location = useLocation();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Extract activeTab from pathname suffix e.g., /account/kontoverifizierung -> kontoverifizierung
  const pathParts = location.pathname.split('/');
  const activeTab = pathParts[2] || (isMobile ? 'menu' : 'meine-daten');

  const isLogged = !!localStorage.getItem('access_token');

  useEffect(() => {
    if (!isMobile && (location.pathname === '/account' || location.pathname === '/account/')) {
      navigate('/account/meine-daten', { replace: true });
    }
  }, [location.pathname, navigate, isMobile]);

  useEffect(() => {
    if (isLogged) {
      setLoading(true);
      getMe()
        .then(setUser)
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
      setUser(null);
    }
  }, [isLogged]);

  useEffect(() => {
    if (isLogged && activeTab === 'transaktionsverlauf') {
      setTxLoading(true);
      getWalletTransactions(50)
        .then((res) => {
          setPayments(res.payments || []);
        })
        .catch(console.error)
        .finally(() => setTxLoading(false));
    }
  }, [isLogged, activeTab]);

  useEffect(() => {
    if (isLogged && (activeTab === 'guthaben' || activeTab === 'auszahlung')) {
      setWalletLoading(true);
      Promise.all([
        getWallet(),
        getWalletTransactions(3)
      ])
        .then(([walletRes, txRes]) => {
          setWallet(walletRes);
          setRecentTx(txRes.payments || []);
        })
        .catch(console.error)
        .finally(() => setWalletLoading(false));
    }
  }, [isLogged, activeTab]);

  useEffect(() => {
    // Reset withdrawal form states on tab change
    setWithdrawAmount('');
    setWithdrawalError(null);
    setCompletedWithdrawal(null);
  }, [activeTab]);

  useEffect(() => {
    if (isLogged && activeTab === 'neuigkeiten') {
      setNotificationsLoading(true);
      getNotifications(50)
        .then(setNotifications)
        .catch(console.error)
        .finally(() => setNotificationsLoading(false));
    }
  }, [isLogged, activeTab]);

  const handleMarkRead = (id: string) => {
    markNotificationRead(id)
      .then((updated) => {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? updated : n))
        );
        window.dispatchEvent(new Event('balance-changed'));
      })
      .catch(console.error);
  };

  const handleMarkAllRead = () => {
    const unread = notifications.filter((n) => !n.read_at);
    if (unread.length === 0) return;
    
    Promise.all(unread.map((n) => markNotificationRead(n.id)))
      .then((updatedList) => {
        setNotifications((prev) =>
          prev.map((n) => {
            const match = updatedList.find((u) => u.id === n.id);
            return match ? match : n;
          })
        );
        window.dispatchEvent(new Event('balance-changed'));
      })
      .catch(console.error);
  };

  const handleWithdrawalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setWithdrawalError(null);

    const amountNum = parseFloat(withdrawAmount);
    const availableCash = parseFloat(wallet?.available_cash || '0');

    if (isNaN(amountNum) || amountNum <= 0) {
      setWithdrawalError(t('account.withdrawal.error.minimum'));
      return;
    }
    if (amountNum < 10) {
      setWithdrawalError(t('account.withdrawal.error.minimum'));
      return;
    }
    if (amountNum > availableCash) {
      setWithdrawalError(t('account.withdrawal.error.insufficient'));
      return;
    }

    setWithdrawalLoading(true);
    createWithdrawal({
      amount: amountNum,
      provider: selectedProvider,
    })
      .then((res) => {
        setCompletedWithdrawal(res);
        // Refresh local wallet/tx history
        if (isLogged) {
          getWallet().then(setWallet).catch(console.error);
        }
        // Emit global event to update balances in header
        window.dispatchEvent(new Event('balance-changed'));
      })
      .catch((err: any) => {
        console.error(err);
        setWithdrawalError(err.message || 'An error occurred during withdrawal request.');
      })
      .finally(() => {
        setWithdrawalLoading(false);
      });
  };

  if (!isLogged) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg shadow-sm border border-gray-200 w-full h-[400px]">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('account.myAccount')}</h2>
        <p className="text-gray-500 mb-6">{t('account.loginPrompt')}</p>
        <button onClick={onLoginClick} className="bg-tipico-red text-white px-8 py-3 rounded text-sm font-bold shadow hover:bg-tipico-red-dark transition">{t('account.loginBtn')}</button>
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/';
  };

  const tabs = [
    { id: 'meine-daten', labelKey: 'account.meineDaten', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { id: 'kontoverifizierung', labelKey: 'account.kontoverifizierung', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'kontoverwaltung', labelKey: 'account.kontoverwaltung', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { id: 'transaktionsverlauf', labelKey: 'account.transaktionsverlauf', icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' },
    { id: 'spacer_1', labelKey: '', icon: '', isSpacer: true },
    { id: 'guthaben', labelKey: 'account.guthaben', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    { id: 'auszahlung', labelKey: 'account.auszahlung', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
    { id: 'spacer_2', labelKey: '', icon: '', isSpacer: true },
    { id: 'einstellungen', labelKey: 'account.einstellungen', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
    { id: 'neuigkeiten', labelKey: 'account.neuigkeiten', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
    { id: 'kontakt', labelKey: 'account.kontakt', icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'spacer_3', labelKey: '', icon: '', isSpacer: true },
    { id: 'abmelden', labelKey: 'account.abmelden', icon: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1', isLogout: true },
  ];

  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row gap-6 w-full items-start mx-auto px-2 lg:px-6">
        {/* Left Sidebar */}
        {(!isMobile || activeTab === 'menu') && (
          <div className="w-full lg:w-[280px] shrink-0 flex flex-col">
            {/* Menu Items grouped in white bg sections */}
            <nav className="flex flex-col text-gray-800 dark:text-gray-200 font-bold bg-white dark:bg-[#1a1a1a] rounded-lg lg:rounded border border-gray-200 dark:border-[#333] lg:border-none shadow-sm lg:shadow-none overflow-hidden divide-y divide-gray-100 dark:divide-[#333] lg:divide-y-0">
              {tabs.map((tab, idx) => (
                tab.isSpacer ? (
                  <div key={`spacer-${idx}`} className="h-4 bg-tipico-gray-light dark:bg-[#121212] border-y border-gray-200/40 dark:border-[#333] lg:h-4 lg:bg-transparent lg:border-none"></div>
                ) : (
                  <button
                    key={tab.id}
                    onClick={() => tab.isLogout ? handleLogout() : navigate(`/account/${tab.id}`)}
                    className={`flex items-center justify-between lg:justify-start gap-4 px-6 py-[22px] lg:py-4 transition text-left bg-white dark:bg-[#1a1a1a] cursor-pointer ${
                      activeTab === tab.id
                        ? 'border-l-4 border-tipico-red text-gray-900 dark:text-white'
                        : 'border-l-4 border-transparent hover:bg-gray-50 dark:hover:bg-[#2a2a2a]'
                    } ${tab.isLogout ? 'text-gray-900 dark:text-white' : ''}`}
                  >
                    <div className="flex items-center gap-4">
                      <svg className={`w-[22px] h-[22px] ${activeTab === tab.id || tab.isLogout ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === tab.id ? 2 : 1.5} d={tab.icon} />
                      </svg>
                      <span className="text-[14px] leading-none">{tab.labelKey ? t(tab.labelKey) : ''}</span>
                    </div>
                    {isMobile && !tab.isLogout && (
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                )
              ))}
              {isMobile && (
                <button
                  onClick={toggleTheme}
                  className="flex items-center justify-between lg:justify-start gap-4 px-6 py-[22px] lg:py-4 transition text-left bg-white dark:bg-[#1a1a1a] cursor-pointer border-l-4 border-transparent hover:bg-gray-50 dark:hover:bg-[#2a2a2a]"
                >
                  <div className="flex items-center gap-4">
                    <svg className="w-[22px] h-[22px] text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {theme === 'dark' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      )}
                    </svg>
                    <span className="text-[14px] leading-none">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                  </div>
                </button>
              )}
            </nav>

            {/* Footer Version */}
            <div className="text-[11px] text-gray-400 mt-6 px-6 pb-4">
              V 3.257.14 m60
            </div>
          </div>
        )}

        {/* Content Area */}
        {(!isMobile || activeTab !== 'menu') && (
          <div className="flex-1 w-full min-h-[500px] overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-20 bg-white rounded shadow-sm">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-tipico-red border-t-transparent" />
            </div>
          ) : activeTab === 'meine-daten' ? (
            <div className="bg-white rounded overflow-hidden shadow-sm">
              {/* Internal Tabs */}
              <div className="flex border-b border-gray-200 bg-white">
                <button 
                  onClick={() => setActiveSubTab('personal')}
                  className={`px-6 py-4 text-[14px] font-bold transition cursor-pointer ${activeSubTab === 'personal' ? 'text-gray-900 border-b-[3px] border-tipico-red' : 'text-gray-500 hover:text-gray-900'}`}>
                  {t('account.userTabs.personal')}
                </button>
                <button 
                  onClick={() => setActiveSubTab('contact')}
                  className={`px-6 py-4 text-[14px] font-bold transition cursor-pointer ${activeSubTab === 'contact' ? 'text-gray-900 border-b-[3px] border-tipico-red' : 'text-gray-500 hover:text-gray-900'}`}>
                  {t('account.userTabs.contact')}
                </button>
                <button 
                  onClick={() => setActiveSubTab('password')}
                  className={`px-6 py-4 text-[14px] font-bold transition cursor-pointer ${activeSubTab === 'password' ? 'text-gray-900 border-b-[3px] border-tipico-red' : 'text-gray-500 hover:text-gray-900'}`}>
                  {t('account.userTabs.password')}
                </button>
              </div>

              <div className="p-8">
                {activeSubTab === 'personal' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.name')}</label>
                      <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200">
                        {user?.username ? `${t('account.salutationMr')} ${user.username}` : `${t('account.salutationMr')} testeer testbyte`}
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.username')}</label>
                      <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200">
                        {user?.username || 'denemexx123'}
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.birthname')}</label>
                      <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200">
                        testbyte
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.birthdate')}</label>
                      <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200">
                        01.01.1980
                      </div>
                    </div>

                    <div className="flex flex-col relative pb-6">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.email')}</label>
                      <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200 flex justify-between items-center">
                        <span>svh88299@laoia.com</span>
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7"></path></svg>
                        </div>
                      </div>
                      <button className="text-[#0066cc] font-medium text-[13px] absolute bottom-0 left-0 hover:underline mt-2 inline-block">
                        {t('account.fields.emailChange')}
                      </button>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.lugasId')}</label>
                      <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200 flex justify-between items-center overflow-hidden">
                        <span className="truncate mr-2">2d4664dd-77db-48ca-a5cb-2b309aad5...</span>
                        <svg className="w-5 h-5 text-gray-400 cursor-pointer hover:text-gray-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}

                {activeSubTab === 'contact' && (
                  <form onSubmit={(e) => { e.preventDefault(); alert(t('account.mockUpdateSuccess') || 'Erfolgreich aktualisiert'); }} className="max-w-md flex flex-col gap-5">
                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.address') || 'Adresse'}</label>
                      <input type="text" defaultValue="Musterstraße 1" className="bg-white text-gray-900 p-3 rounded text-[14px] border border-gray-300 focus:outline-none focus:border-tipico-red transition-colors" />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.city') || 'Stadt'}</label>
                      <input type="text" defaultValue="Berlin" className="bg-white text-gray-900 p-3 rounded text-[14px] border border-gray-300 focus:outline-none focus:border-tipico-red transition-colors" />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.zip') || 'PLZ'}</label>
                      <input type="text" defaultValue="10115" className="bg-white text-gray-900 p-3 rounded text-[14px] border border-gray-300 focus:outline-none focus:border-tipico-red transition-colors" />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.phone') || 'Telefonnummer'}</label>
                      <input type="tel" defaultValue="+49 170 1234567" className="bg-white text-gray-900 p-3 rounded text-[14px] border border-gray-300 focus:outline-none focus:border-tipico-red transition-colors" />
                    </div>
                    <button type="submit" className="bg-tipico-red hover:bg-tipico-red-dark text-white font-bold py-3.5 px-6 rounded-lg text-[13px] shadow transition-colors cursor-pointer mt-2">
                      {t('account.saveChanges') || 'Änderungen speichern'}
                    </button>
                  </form>
                )}

                {activeSubTab === 'password' && (
                  <form onSubmit={(e) => { e.preventDefault(); alert(t('account.mockUpdateSuccess') || 'Erfolgreich aktualisiert'); }} className="max-w-md flex flex-col gap-5">
                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.currentPassword') || 'Aktuelles Passwort'}</label>
                      <input type="password" required className="bg-white text-gray-900 p-3 rounded text-[14px] border border-gray-300 focus:outline-none focus:border-tipico-red transition-colors" />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.newPassword') || 'Neues Passwort'}</label>
                      <input type="password" required className="bg-white text-gray-900 p-3 rounded text-[14px] border border-gray-300 focus:outline-none focus:border-tipico-red transition-colors" />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-gray-900 font-bold mb-2 text-[13px]">{t('account.fields.confirmPassword') || 'Neues Passwort bestätigen'}</label>
                      <input type="password" required className="bg-white text-gray-900 p-3 rounded text-[14px] border border-gray-300 focus:outline-none focus:border-tipico-red transition-colors" />
                    </div>
                    <button type="submit" className="bg-tipico-red hover:bg-tipico-red-dark text-white font-bold py-3.5 px-6 rounded-lg text-[13px] shadow transition-colors cursor-pointer mt-2">
                      {t('account.saveChanges') || 'Änderungen speichern'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          ) : activeTab === 'transaktionsverlauf' ? (
            <div className="bg-white rounded overflow-hidden shadow-sm p-6">
              <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-6 border-b border-gray-100 pb-4">
                {!isMobile && (
                  <h3 className="text-lg font-bold text-gray-900">
                    {t('account.transactions.title')}
                  </h3>
                )}
                
                <div className="flex flex-wrap items-center gap-4">
                  {/* Date range pickers */}
                  <div className="flex items-center gap-2 text-[13px] text-gray-600">
                    <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-2.5 py-1">
                      <label htmlFor="startDate" className="font-semibold text-gray-500">{t('account.transactions.filter.startDate')}</label>
                      <input
                        id="startDate"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="bg-transparent border-none text-gray-800 font-bold focus:outline-none w-[115px]"
                      />
                    </div>
                    <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-2.5 py-1">
                      <label htmlFor="endDate" className="font-semibold text-gray-500">{t('account.transactions.filter.endDate')}</label>
                      <input
                        id="endDate"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="bg-transparent border-none text-gray-800 font-bold focus:outline-none w-[115px]"
                      />
                    </div>
                    {(startDate || endDate) && (
                      <button
                        onClick={() => { setStartDate(''); setEndDate(''); }}
                        className="text-tipico-red hover:text-tipico-red-dark font-bold hover:underline transition cursor-pointer ml-1"
                      >
                        {t('account.transactions.filter.reset')}
                      </button>
                    )}
                  </div>

                  {/* Filters */}
                  <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200">
                    <button
                      onClick={() => setTxFilter('all')}
                      className={`px-4 py-1.5 rounded-md text-[13px] font-bold transition-all cursor-pointer ${
                        txFilter === 'all'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      {t('account.transactions.filter.all')}
                    </button>
                    <button
                      onClick={() => setTxFilter('deposit')}
                      className={`px-4 py-1.5 rounded-md text-[13px] font-bold transition-all cursor-pointer ${
                        txFilter === 'deposit'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      {t('account.transactions.deposit')}
                    </button>
                    <button
                      onClick={() => setTxFilter('withdrawal')}
                      className={`px-4 py-1.5 rounded-md text-[13px] font-bold transition-all cursor-pointer ${
                        txFilter === 'withdrawal'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      {t('account.transactions.withdrawal')}
                    </button>
                  </div>
                </div>
              </div>

              {txLoading ? (
                <div className="flex justify-center items-center py-20 bg-white">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-tipico-red border-t-transparent" />
                </div>
              ) : (() => {
                const filtered = payments.filter((p) => {
                  if (txFilter !== 'all' && p.payment_type !== txFilter) return false;
                  if (startDate) {
                    const txDate = new Date(p.created_at);
                    const filterStart = new Date(startDate);
                    filterStart.setHours(0, 0, 0, 0);
                    if (txDate < filterStart) return false;
                  }
                  if (endDate) {
                    const txDate = new Date(p.created_at);
                    const filterEnd = new Date(endDate);
                    filterEnd.setHours(23, 59, 59, 999);
                    if (txDate > filterEnd) return false;
                  }
                  return true;
                });

                if (filtered.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <p className="text-gray-500 text-sm">{t('account.transactions.noTransactions')}</p>
                    </div>
                  );
                }

                return (
                  <div className="overflow-x-auto w-full">
                    <table className="w-full text-left border-collapse text-[13px]">
                      <thead>
                        <tr className="border-b border-gray-100 text-gray-400 font-bold uppercase tracking-wider text-[11px]">
                          <th className="pb-3 font-semibold">{t('account.transactions.date')}</th>
                          <th className="pb-3 font-semibold">{t('account.transactions.id')}</th>
                          <th className="pb-3 font-semibold">{t('account.transactions.type')}</th>
                          <th className="pb-3 font-semibold">{t('account.transactions.status')}</th>
                          <th className="pb-3 font-semibold text-right">{t('account.transactions.amount')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filtered.map((tx) => {
                          const isDeposit = tx.payment_type === 'deposit';
                          const statusColors: Record<string, string> = {
                            completed: 'bg-green-50 text-green-700 border border-green-200/50',
                            success: 'bg-green-50 text-green-700 border border-green-200/50',
                            pending: 'bg-amber-50 text-amber-700 border border-amber-200/50',
                            failed: 'bg-red-50 text-red-700 border border-red-200/50',
                            declined: 'bg-red-50 text-red-700 border border-red-200/50',
                          };

                          const formatTxStatus = (status: string) => {
                            const key = `account.transactions.status.${status.toLowerCase()}`;
                            const translated = t(key);
                            return translated !== key ? translated : status.charAt(0).toUpperCase() + status.slice(1);
                          };

                          const formatDate = (dateStr: string) => {
                            try {
                              const date = new Date(dateStr);
                              return date.toLocaleString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              });
                            } catch {
                              return dateStr;
                            }
                          };

                          return (
                            <tr key={tx.id} className="hover:bg-gray-50/50 transition-colors">
                              <td className="py-4 text-gray-500 whitespace-nowrap">
                                {formatDate(tx.created_at)}
                              </td>
                              <td className="py-4 text-gray-400 font-mono text-[11px] max-w-[120px] truncate" title={tx.id}>
                                {tx.id}
                              </td>
                              <td className="py-4 font-bold text-gray-800">
                                <span className={`flex items-center gap-2 ${isDeposit ? 'text-green-600' : 'text-red-500'}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${isDeposit ? 'bg-green-500' : 'bg-red-500'}`} />
                                  {isDeposit ? t('account.transactions.deposit') : t('account.transactions.withdrawal')}
                                </span>
                              </td>
                              <td className="py-4">
                                <span className={`px-2 py-1 rounded text-[11px] font-bold uppercase tracking-wider inline-flex items-center justify-center ${
                                  statusColors[tx.status.toLowerCase()] || 'bg-gray-100 text-gray-700 border border-gray-200'
                                }`}>
                                  {formatTxStatus(tx.status)}
                                </span>
                              </td>
                              <td className={`py-4 text-right font-black text-sm whitespace-nowrap ${isDeposit ? 'text-green-600' : 'text-red-600'}`}>
                                {isDeposit ? '+' : '-'}{parseFloat(tx.amount).toFixed(2).replace('.', ',')} €
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          ) : activeTab === 'guthaben' ? (
            <div className="bg-white rounded overflow-hidden shadow-sm p-6">
              {!isMobile && (
                <h3 className="text-lg font-bold text-gray-900 mb-6 border-b border-gray-100 pb-4">
                  {t('account.balance.title')}
                </h3>
              )}

              {walletLoading ? (
                <div className="flex justify-center items-center py-20 bg-white">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-tipico-red border-t-transparent" />
                </div>
              ) : (
                <div className="flex flex-col gap-8">
                  {/* Virtual card & breakdown grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                    {/* Premium Debit Card Visual */}
                    <div 
                      className="w-full h-[220px] rounded-2xl p-6 text-white flex flex-col justify-between shadow-xl relative overflow-hidden select-none"
                      style={{
                        background: 'linear-gradient(135deg, #1f1f1f 0%, #111111 100%)',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      {/* Glossy gradient accent */}
                      <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-tipico-red opacity-15 blur-[60px]" />
                      <div className="absolute -bottom-10 -left-10 w-44 h-44 rounded-full bg-white opacity-5 blur-[60px]" />

                      {/* Card Header */}
                      <div className="flex justify-between items-start">
                        <div className="flex flex-col">
                          <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                            {t('account.balance.premiumWallet')}
                          </span>
                          <span className="text-[12px] font-bold text-gray-300 mt-0.5">
                            {user?.username || 'denemexx123'}
                          </span>
                        </div>
                        <div className="text-[20px] font-black tracking-tighter italic text-white/95">
                          tipico
                        </div>
                      </div>

                      {/* Card Chip & Obfuscated Numbers */}
                      <div className="flex items-center gap-4 my-2">
                        {/* Gold Microchip SVG */}
                        <div className="w-10 h-8 rounded bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-600 opacity-90 p-1 flex flex-col justify-between shrink-0">
                          <div className="flex justify-between h-[30%]">
                            <div className="w-[30%] h-full border-r border-b border-black/20" />
                            <div className="w-[30%] h-full border-l border-b border-black/20" />
                          </div>
                          <div className="flex justify-between h-[30%]">
                            <div className="w-[30%] h-full border-r border-t border-black/20" />
                            <div className="w-[30%] h-full border-l border-t border-black/20" />
                          </div>
                        </div>
                        <span className="font-mono text-[14px] tracking-widest text-gray-400">
                          ••••  ••••  ••••  {wallet?.user_id ? wallet.user_id.slice(-4) : '2026'}
                        </span>
                      </div>

                      {/* Card Footer: Balance */}
                      <div className="flex justify-between items-end">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                            {t('account.balance.total')}
                          </span>
                          <span className="text-[26px] font-black tracking-tight mt-0.5 leading-none">
                            {(() => {
                              const cash = parseFloat(wallet?.available_cash || '0');
                              const bonus = parseFloat(wallet?.available_bonus || '0');
                              return `${(cash + bonus).toFixed(2).replace('.', ',')} €`;
                            })()}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 opacity-80 text-[10px] uppercase font-bold tracking-wider text-gray-400 border border-gray-700/50 bg-black/20 px-2 py-1 rounded-md">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                          Online
                        </div>
                      </div>
                    </div>

                    {/* Breakdown & Quick Actions Column */}
                    <div className="flex flex-col gap-6 w-full">
                      {/* Breakdown Cards */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Echtgeld Breakdown */}
                        <div className="bg-gray-50 border border-gray-200/80 rounded-xl p-4 flex flex-col justify-between h-[100px]">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                            {t('account.balance.realCash')}
                          </span>
                          <div className="flex flex-col mt-2">
                            <span className="text-[16px] font-black text-gray-900 leading-tight">
                              {parseFloat(wallet?.available_cash || '0').toFixed(2).replace('.', ',')} €
                            </span>
                            <span className="text-[10px] text-gray-400 font-medium">
                              {t('account.balance.locked')}: {parseFloat(wallet?.locked_cash || '0').toFixed(2).replace('.', ',')} €
                            </span>
                          </div>
                        </div>

                        {/* Bonus Breakdown */}
                        <div className="bg-gray-50 border border-gray-200/80 rounded-xl p-4 flex flex-col justify-between h-[100px]">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                            {t('account.balance.bonusCash')}
                          </span>
                          <div className="flex flex-col mt-2">
                            <span className="text-[16px] font-black text-gray-900 leading-tight">
                              {parseFloat(wallet?.available_bonus || '0').toFixed(2).replace('.', ',')} €
                            </span>
                            <span className="text-[10px] text-gray-400 font-medium">
                              {t('account.balance.locked')}: {parseFloat(wallet?.locked_bonus || '0').toFixed(2).replace('.', ',')} €
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Quick Actions Buttons */}
                      <div className="flex gap-4">
                        <button 
                          onClick={() => navigate('/account/guthaben')}
                          className="flex-1 bg-tipico-red hover:bg-tipico-red-dark text-white font-bold py-3.5 px-6 rounded-lg text-[13px] shadow transition-colors cursor-pointer text-center"
                        >
                          {t('account.balance.depositAction')}
                        </button>
                        <button 
                          onClick={() => navigate('/account/auszahlung')}
                          className="flex-1 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 font-bold py-3.5 px-6 rounded-lg text-[13px] transition-colors cursor-pointer text-center"
                        >
                          {t('account.balance.withdrawAction')}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity Summary */}
                  <div className="mt-4 border-t border-gray-100 pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-sm font-bold text-gray-900">
                        {t('account.balance.recentActivity')}
                      </h4>
                      <button
                        onClick={() => navigate('/account/transaktionsverlauf')}
                        className="text-[12px] font-bold text-[#0066cc] hover:underline cursor-pointer"
                      >
                        {t('account.balance.viewAllTransactions')}
                      </button>
                    </div>

                    {recentTx.length === 0 ? (
                      <div className="text-center py-6 border border-dashed border-gray-200 rounded-lg text-gray-400 text-xs">
                        {t('account.transactions.noTransactions')}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden bg-gray-50/50">
                        {recentTx.map((tx) => {
                          const isDeposit = tx.payment_type === 'deposit';
                          const formatDate = (dateStr: string) => {
                            try {
                              const d = new Date(dateStr);
                              return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
                            } catch {
                              return dateStr;
                            }
                          };
                          return (
                            <div key={tx.id} className="flex justify-between items-center p-3 bg-white hover:bg-gray-50/40 transition-colors text-xs">
                              <div className="flex items-center gap-3">
                                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                                  isDeposit ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                                }`}>
                                  {isDeposit ? '↓' : '↑'}
                                </span>
                                <div className="flex flex-col">
                                  <span className="font-bold text-gray-800">
                                    {isDeposit ? t('account.transactions.deposit') : t('account.transactions.withdrawal')}
                                  </span>
                                  <span className="text-[10px] text-gray-400">
                                    {formatDate(tx.created_at)} • {tx.provider || 'System'}
                                  </span>
                                </div>
                              </div>
                              <span className={`font-black text-sm ${isDeposit ? 'text-green-600' : 'text-red-600'}`}>
                                {isDeposit ? '+' : '-'}{parseFloat(tx.amount).toFixed(2).replace('.', ',')} €
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'neuigkeiten' ? (
            <div className="bg-white rounded overflow-hidden shadow-sm p-6">
              <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                {!isMobile ? (
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold text-gray-900">
                      {t('account.notifications.title')}
                    </h3>
                    {notifications.filter((n) => !n.read_at).length > 0 && (
                      <span className="bg-tipico-red text-white text-[11px] font-bold px-2 py-0.5 rounded-full animate-pulse">
                        {notifications.filter((n) => !n.read_at).length}
                      </span>
                    )}
                  </div>
                ) : (
                  <div></div>
                )}

                {notifications.filter((n) => !n.read_at).length > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-[12px] font-bold text-tipico-red hover:text-tipico-red-dark hover:underline transition cursor-pointer"
                  >
                    {t('account.notifications.markAllRead')}
                  </button>
                )}
              </div>

              {notificationsLoading ? (
                <div className="flex justify-center items-center py-20 bg-white">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-tipico-red border-t-transparent" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <p className="text-gray-500 text-sm">{t('account.notifications.empty')}</p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {notifications.map((n) => {
                    const isUnread = !n.read_at;
                    const formatted = formatNotification(n, locale);
                    const title = formatted.title;
                    const message = formatted.message;
                    const isBet = n.kind?.startsWith('bet') || n.kind?.includes('ticket');
                    const isWallet = n.kind?.startsWith('wallet') || n.kind?.includes('deposit') || n.kind?.includes('withdrawal');

                    const formatDate = (dateStr: string) => {
                      try {
                        const date = new Date(dateStr);
                        return date.toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        });
                      } catch {
                        return dateStr;
                      }
                    };

                    return (
                      <div 
                        key={n.id}
                        className={`flex justify-between items-start gap-4 p-4 rounded-xl border transition-all ${
                          isUnread 
                            ? 'bg-red-50/15 border-l-4 border-l-tipico-red border-gray-200 shadow-sm'
                            : 'bg-white border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <div className="flex gap-4 items-start flex-1 min-w-0">
                          {/* Notification Icon */}
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                            isUnread 
                              ? 'bg-red-50 text-tipico-red' 
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {isBet ? (
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            ) : isWallet ? (
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                              </svg>
                            )}
                          </div>

                          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[13px] font-black truncate text-gray-900`}>
                                {title}
                              </span>
                              {isUnread && (
                                <span className="w-1.5 h-1.5 rounded-full bg-tipico-red shrink-0" />
                              )}
                            </div>
                            <p className="text-gray-500 text-[12px] leading-relaxed break-words">
                              {message}
                            </p>
                            <span className="text-[10px] text-gray-400 mt-1">
                              {formatDate(n.created_at)}
                            </span>
                          </div>
                        </div>

                        {isUnread && (
                          <button
                            onClick={() => handleMarkRead(n.id)}
                            title={t('account.notifications.markRead')}
                            className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-green-600 transition shrink-0 cursor-pointer"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : activeTab === 'auszahlung' ? (
            <div className="bg-white rounded overflow-hidden shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6 border-b border-gray-100 pb-4">
                {t('account.withdrawal.title')}
              </h3>

              {walletLoading && !wallet ? (
                <div className="flex justify-center items-center py-20 bg-white">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-tipico-red border-t-transparent" />
                </div>
              ) : completedWithdrawal ? (
                /* Success Screen */
                <div className="flex flex-col items-center justify-center py-10 text-center max-w-md mx-auto">
                  <div className="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mb-6 border border-green-200">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    {t('account.withdrawal.successMessage')}
                  </h4>
                  <p className="text-sm text-gray-500 mb-6">
                    {t('account.withdrawal.successSub')}
                  </p>

                  <div className="w-full bg-gray-50 rounded-xl p-4 border border-gray-200 text-left mb-6 flex flex-col gap-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400 font-medium">{t('account.withdrawal.amount')}</span>
                      <span className="text-gray-800 font-black">{parseFloat(completedWithdrawal.amount).toFixed(2).replace('.', ',')} €</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400 font-medium">Payment Method</span>
                      <span className="text-gray-800 font-bold">{completedWithdrawal.provider || selectedProvider}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400 font-medium">{t('account.withdrawal.refId')}</span>
                      <span className="text-gray-500 font-mono select-all truncate max-w-[180px]" title={completedWithdrawal.id}>
                        {completedWithdrawal.id}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setCompletedWithdrawal(null);
                      setWithdrawAmount('');
                    }}
                    className="w-full bg-tipico-red hover:bg-tipico-red-dark text-white font-bold py-3 px-6 rounded-lg text-sm transition shadow cursor-pointer"
                  >
                    {t('account.withdrawal.backToWallet')}
                  </button>
                </div>
              ) : (
                /* Withdrawal Request Form */
                <form onSubmit={handleWithdrawalSubmit} className="flex flex-col gap-6">
                  {/* Balance Display */}
                  <div className="bg-gray-50 border border-gray-200/80 rounded-xl p-4 flex justify-between items-center">
                    <div className="flex flex-col">
                      <span className="text-[12px] font-bold text-gray-500 uppercase tracking-wider">
                        {t('account.withdrawal.availableToWithdraw')}
                      </span>
                      <span className="text-[20px] font-black text-gray-900 mt-1">
                        {parseFloat(wallet?.available_cash || '0').toFixed(2).replace('.', ',')} €
                      </span>
                    </div>
                    <span className="text-[11px] font-bold text-gray-400 bg-gray-200/50 px-2.5 py-1 rounded-md">
                      {t('account.balance.realCash')}
                    </span>
                  </div>

                  {/* Payment Method Selector Grid */}
                  <div className="flex flex-col gap-3">
                    <label className="text-gray-900 font-bold text-[13px]">
                      {t('account.withdrawal.selectMethod')}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Credit Card Option */}
                      <div
                        onClick={() => setSelectedProvider('Credit Card')}
                        className={`border rounded-xl p-4 flex flex-col justify-between h-[120px] cursor-pointer transition-all ${
                          selectedProvider === 'Credit Card'
                            ? 'border-tipico-red bg-red-50/5 ring-1 ring-tipico-red'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          {/* Credit Card Icon */}
                          <svg className="w-8 h-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          {selectedProvider === 'Credit Card' && (
                            <span className="w-4 h-4 rounded-full bg-tipico-red flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col mt-2">
                          <span className="text-xs font-bold text-gray-900">Credit Card</span>
                          <span className="text-[10px] text-gray-400 font-medium">Visa, Mastercard</span>
                        </div>
                      </div>

                      {/* PayPal Option */}
                      <div
                        onClick={() => setSelectedProvider('PayPal')}
                        className={`border rounded-xl p-4 flex flex-col justify-between h-[120px] cursor-pointer transition-all ${
                          selectedProvider === 'PayPal'
                            ? 'border-tipico-red bg-red-50/5 ring-1 ring-tipico-red'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          {/* PayPal Icon Placeholder / SVG */}
                          <span className="font-black text-sm italic text-[#003087]">PayPal</span>
                          {selectedProvider === 'PayPal' && (
                            <span className="w-4 h-4 rounded-full bg-tipico-red flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col mt-2">
                          <span className="text-xs font-bold text-gray-900">PayPal</span>
                          <span className="text-[10px] text-gray-400 font-medium">PayPal Wallet</span>
                        </div>
                      </div>

                      {/* Bank Transfer Option */}
                      <div
                        onClick={() => setSelectedProvider('Bank Transfer')}
                        className={`border rounded-xl p-4 flex flex-col justify-between h-[120px] cursor-pointer transition-all ${
                          selectedProvider === 'Bank Transfer'
                            ? 'border-tipico-red bg-red-50/5 ring-1 ring-tipico-red'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          {/* Bank Icon */}
                          <svg className="w-8 h-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          {selectedProvider === 'Bank Transfer' && (
                            <span className="w-4 h-4 rounded-full bg-tipico-red flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col mt-2">
                          <span className="text-xs font-bold text-gray-900">Bank Transfer</span>
                          <span className="text-[10px] text-gray-400 font-medium">{t('account.withdrawal.bankDays')}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Amount Input */}
                  <div className="flex flex-col gap-3">
                    <label className="text-gray-900 font-bold text-[13px]">
                      {t('account.withdrawal.amount')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="0,00"
                        value={withdrawAmount}
                        onChange={(e) => {
                          const val = e.target.value.replace(',', '.');
                          if (/^\d*\.?\d*$/.test(val)) {
                            setWithdrawAmount(val);
                          }
                        }}
                        className="w-full bg-[#f9f9f9] border border-gray-200 rounded-lg py-3.5 pl-4 pr-12 text-sm font-bold text-gray-900 focus:outline-none focus:border-tipico-red transition"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500">
                        €
                      </span>
                    </div>

                    {/* Quick Select Buttons */}
                    <div className="flex flex-wrap gap-2.5 mt-1">
                      {[10, 20, 50, 100].map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setWithdrawAmount(String(amt))}
                          className="bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-800 font-bold px-4 py-2 rounded-lg text-xs transition cursor-pointer"
                        >
                          +{amt} €
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const available = parseFloat(wallet?.available_cash || '0');
                          setWithdrawAmount(available > 0 ? String(available) : '0');
                        }}
                        className="bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-800 font-black px-4 py-2 rounded-lg text-xs transition cursor-pointer"
                      >
                        MAX
                      </button>
                    </div>
                  </div>

                  {/* Error Indicator */}
                  {withdrawalError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3.5 text-xs text-red-600 font-bold flex items-center gap-2">
                      <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      {withdrawalError}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={
                      withdrawalLoading ||
                      !withdrawAmount ||
                      parseFloat(withdrawAmount) < 10 ||
                      parseFloat(withdrawAmount) > parseFloat(wallet?.available_cash || '0')
                    }
                    className="w-full bg-tipico-red disabled:bg-gray-300 hover:bg-tipico-red-dark text-white font-bold py-4 px-6 rounded-lg text-[13px] shadow transition-colors cursor-pointer flex justify-center items-center gap-2"
                  >
                    {withdrawalLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      t('account.withdrawal.submit')
                    )}
                  </button>
                </form>
              )}
            </div>
          ) : activeTab === 'kontakt' ? (
            <div className="bg-white rounded overflow-hidden shadow-sm p-6 lg:p-10">
              <div className="flex flex-col lg:flex-row gap-10">
                {/* Contact Form */}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 border-b border-gray-100 pb-4">
                    {t('account.kontakt')}
                  </h3>
                  
                  {contactStatus === 'success' ? (
                    <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-8 flex flex-col items-center text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <h4 className="font-black text-xl mb-2 text-gray-900">Nachricht gesendet!</h4>
                      <p className="text-[14px] font-medium text-gray-600 max-w-sm">Vielen Dank für Ihre Anfrage. Unser Support-Team wird sich in Kürze bei Ihnen melden.</p>
                      <button onClick={() => setContactStatus('idle')} className="mt-8 bg-white border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 py-3 px-6 rounded-lg transition text-sm">Neue Nachricht verfassen</button>
                    </div>
                  ) : (
                    <form onSubmit={handleContactSubmit} className="flex flex-col gap-6">
                      <div className="flex flex-col gap-2.5">
                        <label className="text-[13px] font-bold text-gray-900 uppercase tracking-wide">Thema <span className="text-tipico-red">*</span></label>
                        <select 
                          value={contactSubject}
                          onChange={(e) => setContactSubject(e.target.value)}
                          className="bg-[#f9f9f9] border border-gray-200 rounded-lg p-3.5 text-[14px] focus:outline-none focus:border-tipico-red transition font-medium text-gray-800"
                        >
                          <option value="Allgemein">Allgemeine Anfrage</option>
                          <option value="Einzahlung">Einzahlung & Auszahlung</option>
                          <option value="Wetten">Wetten & Auswertung</option>
                          <option value="Konto">Kontoverifizierung</option>
                          <option value="Technisch">Technische Probleme</option>
                        </select>
                      </div>

                      <div className="flex flex-col gap-2.5">
                        <label className="text-[13px] font-bold text-gray-900 uppercase tracking-wide">Nachricht <span className="text-tipico-red">*</span></label>
                        <textarea 
                          value={contactMessage}
                          onChange={(e) => setContactMessage(e.target.value)}
                          placeholder={t('account.supportPlaceholder')}
                          rows={6}
                          className="bg-[#f9f9f9] border border-gray-200 rounded-lg p-3.5 text-[14px] focus:outline-none focus:border-tipico-red transition resize-none font-medium text-gray-800 placeholder:text-gray-400"
                        />
                      </div>

                      <button 
                        type="submit"
                        disabled={!contactMessage.trim() || contactStatus === 'loading'}
                        className="mt-2 bg-tipico-red hover:bg-tipico-red-dark disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-lg text-[14px] shadow transition-colors flex justify-center items-center gap-2"
                      >
                        {contactStatus === 'loading' ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        ) : 'Nachricht absenden'}
                      </button>
                    </form>
                  )}
                </div>

                {/* Contact Info Sidebar */}
                <div className="w-full lg:w-[320px] flex flex-col gap-6">
                  {/* Live Chat */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 flex flex-col">
                    <div className="w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center mb-5 text-tipico-red shadow-sm">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                    </div>
                    <h4 className="font-black text-gray-900 text-[16px]">Live Chat</h4>
                    <p className="text-[13px] text-gray-500 mt-2 mb-5 leading-relaxed font-medium">Unser Support-Team ist täglich von 08:00 bis 02:00 Uhr für Sie da.</p>
                    <button className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 font-bold py-3 px-4 rounded-lg text-[13px] shadow-sm transition w-full flex items-center justify-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                      Chat starten
                    </button>
                  </div>

                  {/* Email Support */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 flex flex-col">
                    <div className="w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center mb-5 text-gray-700 shadow-sm">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </div>
                    <h4 className="font-black text-gray-900 text-[16px]">E-Mail Support</h4>
                    <p className="text-[13px] text-gray-500 mt-2 mb-3 leading-relaxed font-medium">Wir antworten in der Regel innerhalb von 24 Stunden.</p>
                    <a href="mailto:support@alexbet.com" className="text-tipico-red font-bold text-[14px] hover:underline">
                      support@alexbet.com
                    </a>
                  </div>

                  {/* FAQ Link */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 flex flex-col">
                    <h4 className="font-black text-gray-900 text-[16px] mb-2">Hilfebereich & FAQ</h4>
                    <p className="text-[13px] text-gray-500 mb-4 font-medium leading-relaxed">Finden Sie schnelle Antworten auf häufig gestellte Fragen in unserem Hilfe-Center.</p>
                    <button className="text-[#0066cc] font-bold text-[13px] hover:underline text-left inline-flex items-center gap-1">
                      Zum Hilfebereich 
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center max-w-md mx-auto bg-white rounded shadow-sm">
              <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d={tabs.find(t => t.id === activeTab)?.icon} />
              </svg>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t(tabs.find(t => t.id === activeTab)?.labelKey || '')}</h3>
              <p className="text-sm text-gray-500">
                {t('account.underConstruction')}
              </p>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default DesktopAccountPage;
